import { useTranslation } from 'react-i18next'

import { ApplicationAccountUserRole } from '@/common/models/applicationAccountUser'
import {
  AppData,
  StepInfo,
  UserDataWithDetails,
} from '../../models/applicationConnection'
import { translateApplicationAccountUserRole } from '../../utils/translateApplicationAccountUserRole'
import { Spinner } from '../Spinner'
import { StepWrapper } from './StepWrapper'

type UserDataStepProps = {
  appData: AppData
  userData: UserDataWithDetails | null
  error: string | null
  stepInfo: StepInfo
}

export function UserDataStep({
  appData,
  userData,
  error,
  stepInfo,
}: UserDataStepProps) {
  const { t } = useTranslation('common')

  const getStatusMessage = () => {
    if (userData?.isNewUser)
      return t('app_connection.userdata.new_user_message')
    if (userData) return t('app_connection.userdata.existing_user_message')
    return ''
  }

  const getSectionTitle = () => {
    if (userData?.isNewUser) return ''
    if (userData) return t('app_connection.userdata.section_title')
    return ''
  }

  return (
    <StepWrapper appData={appData} stepInfo={stepInfo}>
      {error && (
        <div className="flex items-center justify-center py-8">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {!userData && !error && (
        <div className="flex flex-col items-center justify-center space-y-4 py-8 gap-10">
          <Spinner size="lg" />
          <p className="text-center text-lg text-gray-500 font-semibold">
            {t('app_connection.userdata.loading_message')}
          </p>
        </div>
      )}

      {userData && !error && (
        <>
          <div>
            <p className="text-gray-700 text-base">{getStatusMessage()}</p>
          </div>
          <div>
            {!userData.isNewUser && getSectionTitle() && (
              <p className="text-xl font-semibold my-2">{getSectionTitle()}</p>
            )}
            <p>
              <span className="font-semibold">
                {t('app_connection.userdata.fields.name')}
              </span>{' '}
              {userData.name}
            </p>
            <p>
              <span className="font-semibold">
                {t('app_connection.userdata.fields.cpf')}
              </span>{' '}
              {userData.documentNumber}
            </p>
            <p>
              <span className="font-semibold">
                {t('app_connection.userdata.fields.email')}
              </span>{' '}
              {userData.email}
            </p>
            <p>
              <span className="font-semibold">
                {t('app_connection.userdata.fields.role')}
              </span>{' '}
              {translateApplicationAccountUserRole(
                t,
                userData.role as ApplicationAccountUserRole,
              )}
            </p>
          </div>
        </>
      )}
    </StepWrapper>
  )
}
